import { api } from "@/api/client";
import type { ActivityComparison } from "@/api/types";
import { PanelHelp } from "@/components/PanelHelp";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import { formatDecimal, formatInt, formatPercent } from "@/lib/format";
import { includeTestSessions, selectedTenant } from "@/state/filters";

// "2026-09-24" → "24.09.": Swiss dates, the year given by the series.
const dayLabel = (day: string): string => {
  const [, month, date] = day.split("-");
  return `${date}.${month}.`;
};

const signed = (change: number): string => `${change > 0 ? "+" : ""}${formatPercent(change)}`;

const split = (counts: { signed_in: number; anonymous: number }): string =>
  t("activity.split", {
    signedIn: formatInt(counts.signed_in),
    anonymous: formatInt(counts.anonymous),
  });

/**
 * Who chatted: today so far, the last 7 and 30 complete days against the ones
 * before, and the last 30 days one by one. The panel has its own look-back:
 * the Peak window does not reach it, the partner and test filters do.
 */
export function ActivityPanel() {
  const { data } = usePoll(
    (s) =>
      api.activity({ includeTest: includeTestSessions.value, tenant: selectedTenant.value }, s),
    [includeTestSessions.value, selectedTenant.value],
  );

  const option = data
    ? {
        title: { text: t("activity.chart"), left: "center", textStyle: { fontSize: 14 } },
        tooltip: { trigger: "axis" },
        legend: { bottom: 0 },
        grid: { top: 40, bottom: 48, left: 40, right: 16 },
        xAxis: { type: "category", data: data.daily.map((d) => dayLabel(d.day)) },
        yAxis: { type: "value", minInterval: 1 },
        series: [
          {
            name: t("activity.signedIn"),
            type: "bar",
            stack: "people",
            data: data.daily.map((d) => d.signed_in),
            itemStyle: { color: "#002da5" },
          },
          {
            name: t("activity.anonymous"),
            type: "bar",
            stack: "people",
            data: data.daily.map((d) => d.anonymous),
            itemStyle: { color: "#fab400" },
          },
        ],
      }
    : null;
  // Rendered before the data arrives: the chart mounts once, on the first render.
  const chartRef = useECharts(option);

  return (
    <section class="card">
      <h2>{t("activity.title")}</h2>
      <PanelHelp k="activity.overview" />
      {data && (
        <div class="grid grid-3">
          <div>
            <h3>{t("activity.today")}</h3>
            <div class="kpi">{formatInt(data.today.people)}</div>
            <div class="kpi-sub">{split(data.today)}</div>
          </div>
          <Period label="activity.week" comparison={data.week} />
          <Period label="activity.month" comparison={data.month} />
        </div>
      )}
      <div ref={chartRef} class="chart" />
      <div class="kpi-sub">{t("activity.scope")}</div>
      {data && !data.complete && (
        <div class="kpi-sub limit-warn">{t("activity.incomplete")}</div>
      )}
    </section>
  );
}

function Period({ label, comparison }: { label: TranslationKey; comparison: ActivityComparison }) {
  const { current, previous, change } = comparison;
  return (
    <div>
      <h3>{t(label)}</h3>
      <div class="kpi">{formatDecimal(current.avg_per_day)}</div>
      <div class="kpi-sub">{t("activity.perDay")}</div>
      <div class="kpi-sub">
        {change == null
          ? t("activity.noPrevious", { days: current.days })
          : t("activity.change", {
              change: signed(change),
              previous: formatDecimal(previous.avg_per_day),
              days: current.days,
            })}
      </div>
      <div class="kpi-sub">
        {t("activity.people", { n: formatInt(current.people) })} · {split(current)}
      </div>
    </div>
  );
}
